// MCHD Room Scheduler - Session Management Hook
// Custom hook for managing user sessions

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { 
  getUserSessions,
  terminateSession, 
  terminateAllSessions, 
  updateSessionActivity
} from '../../api/endpoints/auth';

/**
 * Custom hook for managing user sessions
 * @returns Session management state and functions
 */
export function useSessionManagement() {
  const queryClient = useQueryClient();

  // Get sessions query
  const { 
    data: sessionsData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['sessions'],
    queryFn: getUserSessions,
    staleTime: 30 * 1000, // 30 seconds - sessions change frequently
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Terminate specific session mutation
  const terminateSessionMutation = useMutation({
    mutationFn: terminateSession,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
        notifications.show({
          title: 'Success',
          message: 'Session terminated successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to terminate session',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to terminate session',
        color: 'red',
      });
    },
  });

  // Terminate all sessions mutation
  const terminateAllSessionsMutation = useMutation({
    mutationFn: terminateAllSessions,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
        notifications.show({
          title: 'Success',
          message: 'All sessions terminated successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.error?.message || 'Failed to terminate all sessions',
          color: 'red',
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to terminate all sessions',
        color: 'red',
      });
    },
  });

  // Update session activity mutation
  const updateActivityMutation = useMutation({
    mutationFn: updateSessionActivity,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
      }
    },
    onError: (error) => {
      console.error('Failed to update session activity:', error);
    },
  });

  // Calculate session statistics
  const sessions = sessionsData?.data?.sessions || [];
  const activeSessions = sessions.filter(session => session.isActive);
  const inactiveSessions = sessions.filter(session => !session.isActive);
  const recentSessions = sessions.filter(session => {
    const lastActivity = new Date(session.lastActivity);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return lastActivity > fiveMinutesAgo;
  });

  // Get unique IP addresses
  const uniqueIPs = [...new Set(sessions.map(session => session.ipAddress))];

  // Get unique devices
  const uniqueDevices = [...new Set(sessions.map(session => session.deviceInfo).filter(Boolean))];

  return {
    // Data
    sessions,
    activeSessions,
    inactiveSessions,
    recentSessions,
    uniqueIPs,
    uniqueDevices,
    isLoading,
    error: error?.message || null,
    
    // Actions
    terminateSession: terminateSessionMutation.mutate,
    terminateAllSessions: terminateAllSessionsMutation.mutate,
    updateActivity: updateActivityMutation.mutate,
    
    // Loading states
    isTerminating: terminateSessionMutation.isPending,
    isTerminatingAll: terminateAllSessionsMutation.isPending,
    isUpdatingActivity: updateActivityMutation.isPending,
    
    // Statistics
    stats: {
      total: sessions.length,
      active: activeSessions.length,
      inactive: inactiveSessions.length,
      recent: recentSessions.length,
      uniqueIPs: uniqueIPs.length,
      uniqueDevices: uniqueDevices.length,
    },
    
    // Refetch function
    refetch: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  };
}
